'use client';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { createPolymorphicComponent, Slot } from '@s7/base';
import { MilesRound } from '@s7/features';
import { NewSlimProfileLinear } from '@s7/icons';
import { Flex, Stack } from '@s7/layout';
import { composeHandlers } from '@s7/react-utils';
import { packSX } from '@s7/styles';
import { Skeleton, Button, Typography, Avatar } from '@s7/ui';
import { useAnalytics } from '../../analytics.js';
import { profileStyles } from './HeaderProfile.styles.js';
import { getName } from './utils/getName/getName.js';
import { useShadowStyle } from '../../hooks/useShadowStyle/useShadowStyle.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const _HeaderProfile = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _profile_firstName, _profile_lastName;
    const { sx, profile, loginLabel, loading, fallback, onClick } = props, others = _object_without_properties_loose(props, [
        "sx",
        "profile",
        "loginLabel",
        "loading",
        "fallback",
        "onClick"
    ]);
    const shadowStyle = useShadowStyle();
    const headerTopic = useAnalytics('header');
    const dispatchEvent = profile ? headerTopic.goProfile : headerTopic.goLogin;
    if (loading) {
        return fallback ? /*#__PURE__*/ jsx(Slot, {
            component: fallback
        }) : /*#__PURE__*/ jsx(Skeleton, {
            accentColor: "background.highlight",
            color: "background.content",
            height: 48,
            radius: "1.5r",
            sx: shadowStyle,
            width: 96
        });
    }
    return /*#__PURE__*/ jsx(Button, _extends({
        ref: ref,
        sx: [
            profileStyles(profile),
            shadowStyle,
            ...packSX(sx)
        ],
        variant: "white",
        onClick: composeHandlers(onClick, dispatchEvent)
    }, others, {
        children: /*#__PURE__*/ jsx(Flex, {
            align: "center",
            spacing: profile ? '2s' : '1.5s',
            children: profile ? /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    profile.firstName && /*#__PURE__*/ jsxs(Stack, {
                        align: "start",
                        spacing: 0,
                        children: [
                            /*#__PURE__*/ jsx(Typography, {
                                size: "xs2",
                                weight: "semibold",
                                children: getName(profile.firstName, profile.lastName)
                            }),
                            typeof profile.miles === 'number' && /*#__PURE__*/ jsx(MilesRound, {
                                amount: profile.miles.toLocaleString(),
                                size: "xs1"
                            })
                        ]
                    }),
                    profile.firstName || profile.avatarUrl ? /*#__PURE__*/ jsx(Avatar, {
                        borderColor: "border.divider",
                        imgSrc: profile.avatarUrl,
                        size: 36,
                        withBorders: true,
                        children: /*#__PURE__*/ jsxs(Typography, {
                            color: "text.medium",
                            size: "xs2",
                            variant: "text",
                            weight: "semibold",
                            children: [
                                ((_profile_firstName = profile.firstName) == null ? void 0 : _profile_firstName[0]) || '',
                                ((_profile_lastName = profile.lastName) == null ? void 0 : _profile_lastName[0]) || ''
                            ]
                        })
                    }) : /*#__PURE__*/ jsx(NewSlimProfileLinear, {
                        color: "text.medium",
                        size: 20
                    })
                ]
            }) : /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx(Typography, {
                        size: "xs2",
                        weight: "semibold",
                        children: loginLabel
                    }),
                    /*#__PURE__*/ jsx(NewSlimProfileLinear, {
                        color: "text.primary",
                        size: 20
                    })
                ]
            })
        })
    }));
});
const HeaderProfile = createPolymorphicComponent(_HeaderProfile);

export { HeaderProfile };
//# sourceMappingURL=HeaderProfile.js.map
